import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Routes a Student is allowed to visit while their onboarding is
// incomplete (no profile, awaiting review, or awaiting distribution).
// The student MUST be able to complete their data and to take the
// placement test, but everything else (dashboard, classrooms,
// assignments, badges, reports, leaderboard, …) is hidden until the
// admin distributes them to a classroom. Defense-in-depth: the
// backend dashboard endpoint also returns 403 with `code:
// student_onboarding_incomplete` for the same cases.
const STUDENT_ONBOARDING_ALLOWED_PATHS = [
  '/student-onboarding',
  '/register',
  '/legacy-student',
  '/auth/callback',
  '/login',
  '/unauthorized',
  '/change-password',
  '/placement-test',
  '/settings',
  '/privacy',
  '/terms',
];

const isOnboardingAllowed = (pathname) => {
  if (!pathname) return false;
  return STUDENT_ONBOARDING_ALLOWED_PATHS.some(
    (p) => pathname === p || pathname.startsWith(p + '/')
  );
};

export const ProtectedRoute = ({ children, allowedRoles = [], permission = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FCFCFA]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A1128] mx-auto"></div>
          <p className="mt-4 text-[#5C6B8C]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  // Phase 7.5 — force change-password when flag is set, except on the page itself.
  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // ─── Student Onboarding Guard (Feb 2026, relaxed Jun 2026) ───────
  // Only the `profile` stage (real data still missing) blocks access to
  // the platform. Students whose application is still under admin
  // review OR pending classroom distribution land directly on the
  // dashboard and see their status as an inline banner there.
  if (user.role === 'Student') {
    const stage = user.student_onboarding_stage;
    const profileIncomplete = user.needs_profile_completion === true && stage === 'profile';
    if (profileIncomplete && !isOnboardingAllowed(location.pathname)) {
      return <Navigate to="/register?step=profile" replace />;
    }
  }

  // Phase 8 (Student Onboarding) — Force pending placement test before
  // anything else. Only Students who opted into the placement-test branch
  // during registration AND have not finished it yet are redirected. They
  // can still navigate to /change-password (handled above) and to a few
  // safe utility routes (/login, /unauthorized).
  if (
    user.role === 'Student'
    && user.placement_test_required === true
    && !user.placement_test_completed
    && location.pathname !== '/placement-test'
    && location.pathname !== '/change-password'
    && location.pathname !== '/unauthorized'
    && location.pathname !== '/student-onboarding'
    && !location.pathname.startsWith('/family-link')
  ) {
    return <Navigate to="/placement-test" replace />;
  }

  // ─── PBAC Gate (Feb 2026 — Phase 1) ───────────────────────────────
  // The `permission` prop is the source of truth when present. A user
  // whose permission_profile has the key === true is allowed in,
  // REGARDLESS of their role string. Super_Admin always passes. This
  // is what unblocks custom-permission users (e.g. an Academic
  // Supervisor granted finance access) from hitting /unauthorized on
  // pages whose `allowedRoles` list is still legacy/role-based.
  //
  // When no `permission` is provided we fall back to the legacy
  // `allowedRoles` check so persona-specific routes (Student-only
  // placement test, Parent-only My Children, etc.) keep working.
  if (permission) {
    if (user.role === 'Super_Admin') return children;
    const profile = user.permission_profile || {};
    if (profile[permission] === true) return children;
    // Legacy fallback — array-shaped permissions
    if (Array.isArray(user.permissions) && user.permissions.includes(permission)) {
      return children;
    }
    return <Navigate to="/unauthorized" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};
